import React, { useState, useCallback, useEffect, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";

import LoadingSpinner from "./shared/components/UIElements/LoadingSpinner";
// Wrap routes into the suspense component from react and add fallback={}

import { AuthContext } from "./shared/context/auth-context";
import Auth from "./user/pages/Auth";
import IdleLogout from "./shared/components/Session/IdleLogout";
import SessionNotice from "./shared/components/Session/SessionNotice";
import {
  awayAtStripe, clearActivity, clearAwayAtStripe, clearLogoutReason, idleState, markActivity, setLogoutReason,
} from "./shared/util/idle";
import { canManagePortfolio } from "./shared/util/roles";

import MainNavigation from "./shared/components/Navigation/MainNavigation";

import Frontpage from "./dashboard/pages/Frontpage";
import Trades from "./dashboard/pages/Trades";
import InstrumentDetails from "./dashboard/pages/InstrumentDetails";
import Scanner from "./dashboard/pages/Scanner";
import Reports from "./dashboard/pages/Reports";
import AdminPage from "./dashboard/pages/AdminPage";
import CheckoutSuccess from "./dashboard/pages/CheckoutSuccess";
import CheckoutCancel from "./dashboard/pages/CheckoutCancel";
import PerformanceComparison from "./dashboard/components/PerformanceComparison";

import Footer from "./shared/components/Footer"; // Import the new Footer component
import UserSettings from "./dashboard/pages/UserSettings";
import LiveFund from "./dashboard/pages/LiveFund";

const NewInstrument = React.lazy(() =>
  import("./dashboard/pages/NewInstrument")
); // code splitting

const UpdateInstruments = React.lazy(() =>
  import("./dashboard/pages/UpdateInstruments")
); // code splitting

let logoutTimer;

const App = () => {

  const buildVersion = `${process.env.REACT_APP_TAG}` || 'N/A';
  const buildDate = `${process.env.REACT_APP_BUILD_DATE}` || 'N/A';
  const containerName = "© 2026 Maple Tree Technologies X. All rights reserved.";

  const [token, setToken] = useState(false);
  const [tokenExpirationDate, setTokenExpirationDate] = useState();
  const [userId, setUserId] = useState(false);
  const [role, setRole] = useState(null);
  const [isPaid, setIsPaid] = useState(false);
  const [paidUntil, setPaidUntil] = useState(null);
  // False until the saved session has been checked. Until then no routes are
  // rendered, so reloading a logged-in page (e.g. /admin/users) doesn't fall
  // through to the logged-out routes and redirect to "/".
  const [sessionChecked, setSessionChecked] = useState(false);

  const login = useCallback((uid, token, expirationDate, role, isPaid, paidUntil, name) => {
    markActivity();
    clearLogoutReason();
    setToken(token);
    setUserId(uid);
    setRole(role || "user");
    setIsPaid(isPaid || false);
    setPaidUntil(paidUntil || null);
    const tokenExpirationDate =
      expirationDate || new Date(new Date().getTime() + 1000 * 60 * 60);

    setTokenExpirationDate(tokenExpirationDate);
    localStorage.setItem(
      "userData",
      JSON.stringify({
        userId:     uid,
        name:       name || null,
        token:      token,
        expiration: tokenExpirationDate.toISOString(),
        role:       role || "user",
        isPaid:     isPaid || false,
        paidUntil:  paidUntil || null,
      })
    );
  }, []);

  // Called after a successful subscription payment (or mock) to update
  // isPaid/paidUntil in both React state and localStorage without a full re-login.
  const updateSubscription = useCallback((newIsPaid, newPaidUntil) => {
    setIsPaid(newIsPaid);
    setPaidUntil(newPaidUntil);
    const stored = JSON.parse(localStorage.getItem("userData") || "{}");
    localStorage.setItem(
      "userData",
      JSON.stringify({ ...stored, isPaid: newIsPaid, paidUntil: newPaidUntil })
    );
  }, []);

  // An admin switches the portfolio manager role in the user list; the
  // backend applies it at once, the navigation from the next page load.
  const updateRole = useCallback((newRole) => {
    setRole(newRole || "user");
    const stored = JSON.parse(localStorage.getItem("userData") || "{}");
    localStorage.setItem("userData", JSON.stringify({ ...stored, role: newRole || "user" }));
  }, []);

  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem("userData"));
    if (
      storedData &&
      storedData.token &&
      new Date(storedData.expiration) > new Date()
    ) {
      // Back from Stripe: the time there doesn't count. Otherwise a session
      // left alone for longer than the inactivity logout (shared/util/idle.js)
      // ends here, e.g. after the tab was closed.
      if (awayAtStripe()) {
        clearAwayAtStripe();
        markActivity();
      }
      if (idleState().phase === "expired") {
        localStorage.removeItem("userData");
        clearActivity();
        setLogoutReason("idle");
        setSessionChecked(true);
        return;
      }
      login(
        storedData.userId,
        storedData.token,
        new Date(storedData.expiration),
        storedData.role,
        storedData.isPaid,
        storedData.paidUntil || null,
        storedData.name || null
      );

      // Refresh subscription state and role from DB so a Stripe webhook that
      // fired while the user was away, or a role an admin switched, is
      // reflected immediately on next page load.
      fetch("/api/users/me", {
        headers: { Authorization: "Bearer " + storedData.token },
      })
        .then((res) => res.ok ? res.json() : null)
        .then((data) => {
          if (!data) return;
          updateSubscription(data.isPaid, data.paidUntil);
          updateRole(data.role);
        })
        .catch(() => {});
    }
    setSessionChecked(true);
  }, [login]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = useCallback(() => {
    setToken(null);
    setUserId(null);
    setRole(null);
    setIsPaid(false);
    setPaidUntil(null);
    localStorage.removeItem("userData");
    clearActivity();
  }, []);

  // A logout in another tab (by hand or for inactivity) logs this tab out too
  useEffect(() => {
    const onStorage = (event) => {
      if ((event.key === "userData" || event.key === null) && !localStorage.getItem("userData")) logout();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [logout]);

  useEffect(() => {
    if (token && tokenExpirationDate) {
      const remainingTokenTime =
        tokenExpirationDate.getTime() - new Date().getTime();
      logoutTimer = setTimeout(logout, remainingTokenTime);
    } else {
      clearTimeout(logoutTimer);
    }
  }, [token, logout, tokenExpirationDate]);

  let routes = null;

  if (!sessionChecked) {
    // first render only; the effect above runs right after it
  } else if (token) {
    // Admins and portfolio managers only; the backend refuses their data to
    // everyone else. The redirect has to stay explicit, or "/portfolio" would
    // match ":iid".
    const portfolioOnly = (page) => (canManagePortfolio(role) ? page : <Navigate to="/" replace />);
    routes = (
      <Routes>
        <Route path="/" element={<PerformanceComparison />} />
        <Route path="/portfolio" element={portfolioOnly(<Trades />)} />
        <Route path="/live" element={<LiveFund />} />
        <Route path="/scanner" element={<Scanner />} />
        <Route path="/usersettings" element={<UserSettings />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/admin/users" element={<AdminPage />} />
        <Route path="/updateinstruments" element={<UpdateInstruments />} />
        <Route path="/newinstrument" element={<NewInstrument />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/checkout/cancel"  element={<CheckoutCancel />} />
        <Route path="/auth" element={<Navigate to="/" />} />
        <Route path=":iid" element={portfolioOnly(<InstrumentDetails />)} />
      </Routes>
    );
  } else {
    routes = (
      <Routes>
        <Route path="/" element={<Frontpage />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/checkout/cancel"  element={<CheckoutCancel />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn: !!token,
        token,
        userId,
        role,
        isPaid,
        paidUntil,
        login,
        logout,
        updateSubscription,
      }}
    >
      <Router>
        <MainNavigation />
        {token ? <IdleLogout /> : sessionChecked && <SessionNotice />}
        <main>
          <Suspense
            fallback={
              <div className="center">
                <LoadingSpinner asOverlay message="Loading "/>
              </div>
            }
          >
            {routes}
          </Suspense>
        </main>
        <Footer buildVersion={buildVersion} containerName={containerName} buildDate={buildDate} />
      </Router>
    </AuthContext.Provider>
  );
};

export default App;
